import React from "react";
import { useRouteError, isRouteErrorResponse, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ErrorFallback from "../error-fallback";

const RouteError: React.FC = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleReset = () => {
    navigate(0); // Refresh the current route
  };

  // Handle different types of route errors
  if (isRouteErrorResponse(error)) {
    let title: string;
    let message: string;

    switch (error.status) {
      case 404:
        title = t("error.notFound.title", "Page Not Found");
        message = t("error.notFound.message", "The page you're looking for doesn't exist.");
        break;
      case 403:
        title = t("error.forbidden.title", "Access Denied");
        message = t("error.forbidden.message", "You don't have permission to access this page.");
        break;
      case 500:
        title = t("error.serverError.title", "Server Error");
        message = t("error.serverError.message", "Something went wrong on our end. Please try again later.");
        break;
      default:
        title = t("error.generic.title", "Something went wrong");
        message = error.statusText || error.data?.message || t("error.generic.message", "An unexpected error occurred.");
    }

    return (
      <ErrorFallback
        title={title}
        message={message}
        resetError={handleReset}
        showDetails={false}
        showReload={true}
        showHome={true}
      />
    );
  }

  // Handle JavaScript errors — show as 404 in production to avoid exposing internals
  if (error instanceof Error) {
    return (
      <ErrorFallback
        error={error}
        resetError={handleReset}
        showDetails={process.env.NODE_ENV === 'development'}
        showReload={true}
        showHome={true}
        showAsNotFound={process.env.NODE_ENV !== 'development'}
      />
    );
  }

  // Handle unknown errors — show as 404 in production
  return (
    <ErrorFallback
      resetError={handleReset}
      showDetails={false}
      showReload={true}
      showHome={true}
      showAsNotFound={process.env.NODE_ENV !== 'development'}
    />
  );
};

export default RouteError;